import { apiRequest } from './apiService';

const toCount = (value) => {
  const count = Number(value || 0);
  return Number.isFinite(count) ? count : 0;
};

const normalizeArtworkCounts = (counts = {}) => ({
  drafts: toCount(counts.drafts),
  published: toCount(counts.published),
});

const normalizeConnectStatus = (data = {}) => {
  const connect = data.stripeConnect || data.connect || null;
  return connect && typeof connect === 'object' ? connect : null;
};

const normalizeMetrics = (data = {}) => ({
  pieces_sold: Number(data.pieces_sold || data.metrics?.pieces_sold || 0),
  total_value_sold: Number(data.total_value_sold || data.metrics?.total_value_sold || 0),
  platform_profit: Number(data.platform_profit || data.metrics?.platform_profit || 0),
});

const normalizeArtistListItem = (data = {}) => ({
  id: data.id,
  user_id: data.user_id || '',
  user_display_name: data.user_display_name || data.artistName || 'Unknown artist',
  user_email: data.user_email || data.email || '',
  account_active: Boolean(data.account_active),
  profile_completed: data.profile_completed === true,
  isTestArtist: data.isTestArtist === true,
  connectReady: data.connectReady === true,
  stripeConnectAccountId: data.stripeConnectAccountId || '',
  stripeConnect: normalizeConnectStatus(data),
  connect: normalizeConnectStatus(data),
  onboarding_status: data.onboarding_status || '',
  artwork_counts: normalizeArtworkCounts(data.artwork_counts),
});

const normalizeArtistDetail = (data = {}) => ({
  id: data.id,
  user_id: data.user_id || '',
  user_display_name: data.user_display_name || data.artistName || 'Unknown artist',
  user_email: data.user_email || data.email || '',
  account_active: Boolean(data.account_active),
  isTestArtist: data.isTestArtist === true,
  connectReady: data.connectReady === true,
  phone_number: data.phone_number || '',
  preferred_currency: data.preferred_currency || '',
  country: data.country || '',
  city: data.city || '',
  last_login_at: data.last_login_at || null,
  created_at: data.created_at || null,
  updated_at: data.updated_at || null,
  roles: Array.isArray(data.roles) ? data.roles : [],
  profile_completed: Boolean(data.profile_completed),
  payout_method: data.payout_method || '',
  bio: data.bio || '',
  portfolio_url: data.portfolio_url || '',
  instagram_handle: data.instagram_handle || '',
  metrics: normalizeMetrics(data),
});

export const normalizeTestArtist = (data = {}) => ({
  id: data.id || data.artistId,
  artistId: data.artistId || data.id,
  artistName: data.artistName || data.user_display_name || 'Unknown artist',
  email: data.email || data.user_email || '',
  account_active: Boolean(data.account_active),
  connectReady: data.connectReady === true,
  addedBy: data.addedBy || '',
  createdAt: data.createdAt || null,
  updatedAt: data.updatedAt || null,
});

export const fetchArtists = async () => {
  try {
    const artists = await apiRequest('/admin/artists') || [];
    return artists.map(normalizeArtistListItem);
  } catch (error) {
    console.error('Failed to fetch artists:', error);
    throw new Error('Unable to load artists');
  }
};

export const fetchArtistById = async (artistId) => {
  if (!artistId) {
    throw new Error('Artist ID is required');
  }

  try {
    const data = await apiRequest(`/admin/artists/${encodeURIComponent(artistId)}`);
    return normalizeArtistDetail(data);
  } catch (error) {
    console.error(`Failed to fetch artist ${artistId}:`, error);
    throw new Error(error.message || 'Unable to load artist details');
  }
};

export const updateArtistAccountStatus = async (artistId, isActive) => {
  if (!artistId) {
    throw new Error('Artist ID is required');
  }

  try {
    const accountActive = Boolean(isActive);
    return await apiRequest(`/admin/artists/${encodeURIComponent(artistId)}/status`, {
      method: 'PATCH',
      body: {
        account_active: accountActive,
        ...(accountActive ? { manual_override: true } : {}),
      },
    });
  } catch (error) {
    console.error(`Failed to update artist ${artistId} status:`, error);
    throw new Error(error.message || 'Unable to update artist status');
  }
};

export const fetchTestArtists = async () => {
  try {
    const testArtists = await apiRequest('/admin/test-artists') || [];
    return testArtists.map(normalizeTestArtist);
  } catch (error) {
    console.error('Failed to fetch test artists:', error);
    throw new Error(error.message || 'Unable to load test artists');
  }
};

export const addTestArtist = async (artistId) => {
  if (!artistId) {
    throw new Error('Artist ID is required');
  }

  try {
    return await apiRequest(`/admin/artists/${encodeURIComponent(artistId)}/test-artist`, {
      method: 'POST',
    });
  } catch (error) {
    console.error(`Failed to add artist ${artistId} to the test group:`, error);
    throw new Error(error.message || 'Unable to add artist to the test group');
  }
};

export const removeTestArtist = async (artistId) => {
  if (!artistId) {
    throw new Error('Artist ID is required');
  }

  try {
    return await apiRequest(`/admin/artists/${encodeURIComponent(artistId)}/test-artist`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error(`Failed to remove artist ${artistId} from the test group:`, error);
    throw new Error(error.message || 'Unable to remove artist from the test group');
  }
};

export const deleteArtist = async (artistId) => {
  if (!artistId) {
    throw new Error('Artist ID is required');
  }

  try {
    return await apiRequest(`/admin/artists/${encodeURIComponent(artistId)}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error(`Failed to delete artist ${artistId}:`, error);
    throw new Error(error.message || 'Unable to delete artist');
  }
};

export const approveArtistRegistration = async (registrationId) => {
  if (!registrationId) {
    throw new Error('Registration ID is required');
  }

  return apiRequest(`/admin/artist-registrations/${encodeURIComponent(registrationId)}/approve`, {
    method: 'POST',
  });
};

export const rejectArtistRegistration = async (registrationId) => {
  if (!registrationId) {
    throw new Error('Registration ID is required');
  }

  return apiRequest(`/admin/artist-registrations/${encodeURIComponent(registrationId)}/reject`, {
    method: 'POST',
  });
};
